import { useMemo, useState, type FormEvent } from 'react';
import {
  V2Button,
  V2FormGrid,
  V2FormField,
  Icon,
  V2CloseButton,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { systemClock } from '@sakhtar/calendar';
import type {
  AccountListResponse,
  FiscalPeriodListResponse,
  OpenItemListResponse,
} from '../../lib/types/finance.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { formatRialFa, parseRialInput, type AmountDisplayUnit } from '../../lib/finance-display.js';
import { toEnglishDigits } from '../../lib/phoneNumber.js';

export interface SalesReturnFormLine {
  readonly productId: string;
  readonly description: string;
  readonly quantity: string;
  readonly unitPriceRial: string;
  readonly vatAmountRial: string;
}

const EMPTY_RETURN_LINE: SalesReturnFormLine = {
  productId: '',
  description: '',
  quantity: '1',
  unitPriceRial: '',
  vatAmountRial: '',
};

const COMMON_RETURN_REASONS = [
  'معیوب بودن کالا یا خرابی در حین حمل',
  'مغایرت کالا با پیش‌فاکتور یا سفارش خرید',
  'انصراف خریدار طبق شرایط بازگشت کالا',
  'عدم تایید کیفیت و برگشت از خط تولید',
  'تخفیف توافقی و تعدیل قیمت پس از فروش',
];

export function SalesReturnCreateModal({
  open,
  onClose,
  onSuccess,
  slug,
  base,
  unit,
  unitLabel,
  initialPartyId,
  initialOpenItemId,
  initialInvoiceReference,
  initialControlAccountId,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly slug: string;
  readonly base: string;
  readonly unit: AmountDisplayUnit;
  readonly unitLabel: string;
  readonly initialPartyId?: string | undefined;
  readonly initialOpenItemId?: string | undefined;
  readonly initialInvoiceReference?: string | undefined;
  readonly initialControlAccountId?: string | undefined;
}) {
  const today = String(systemClock.today());
  const [partyId, setPartyId] = useState(initialPartyId ?? '');
  const [originalOpenItemId, setOriginalOpenItemId] = useState(initialOpenItemId ?? '');
  const [originalInvoiceReference, setOriginalInvoiceReference] = useState(
    initialInvoiceReference ?? '',
  );
  const [businessDate, setBusinessDate] = useState<string>(today);
  const [fiscalPeriodId, setFiscalPeriodId] = useState('');
  const [returnReason, setReturnReason] = useState(COMMON_RETURN_REASONS[0]!);
  const [controlAccountId, setControlAccountId] = useState(initialControlAccountId ?? '');
  const [salesReturnAccountId, setSalesReturnAccountId] = useState('');
  const [vatAccountId, setVatAccountId] = useState('');
  const [restock, setRestock] = useState(true);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<SalesReturnFormLine[]>([{ ...EMPTY_RETURN_LINE }]);
  const [autoComputeTax, setAutoComputeTax] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Master data queries
  const parties = useApiGet<MasterDataListResponse>(
    `${base}/api/v1/master-data/parties?role=customer&limit=200`,
    [slug],
  );
  const glAccounts = useApiGet<AccountListResponse>(`${base}/api/v1/finance/accounts`, [slug]);
  const fiscalPeriods = useApiGet<FiscalPeriodListResponse>(
    `${base}/api/v1/accounting/fiscal-periods`,
    [slug],
  );
  const products = useApiGet<MasterDataListResponse>(
    `${base}/api/v1/master-data/products?status=active`,
    [slug],
  );
  const openInvoices = useApiGet<OpenItemListResponse>(
    partyId ? `${base}/api/v1/finance/ar/open-items?partyId=${partyId}` : null,
    [slug, partyId],
  );

  const postableAccounts = useMemo(
    () =>
      glAccounts.status === 'ready'
        ? [...glAccounts.data.items]
            .filter((a) => a.isPostable && a.status === 'active')
            .sort((a, b) => a.code.localeCompare(b.code))
        : [],
    [glAccounts],
  );

  // Auto-select sensible accounts if not already chosen
  useMemo(() => {
    if (postableAccounts.length === 0) return;

    if (!controlAccountId) {
      // Find trade receivables (103... or name contains دریافتنی)
      const arAcc = postableAccounts.find(
        (a) => a.code.startsWith('103') || a.name.includes('دریافتنی تجاری'),
      );
      if (arAcc) setControlAccountId(arAcc.accountId);
    }

    if (!salesReturnAccountId) {
      // Find sales return account (80... or name contains برگشت از فروش)
      const retAcc =
        postableAccounts.find((a) => a.name.includes('برگشت از فروش')) ??
        postableAccounts.find((a) => a.code.startsWith('80') || a.code.startsWith('402')) ??
        postableAccounts.find((a) => a.name.includes('فروش'));
      if (retAcc) setSalesReturnAccountId(retAcc.accountId);
    }

    if (!vatAccountId) {
      // Find VAT account (202... or name contains ارزش افزوده)
      const vatAcc = postableAccounts.find(
        (a) => a.code.startsWith('202') || a.name.includes('ارزش افزوده'),
      );
      if (vatAcc) setVatAccountId(vatAcc.accountId);
    }
  }, [postableAccounts, controlAccountId, salesReturnAccountId, vatAccountId]);

  // Auto-select open fiscal period for current date
  useMemo(() => {
    if (fiscalPeriods.status !== 'ready' || fiscalPeriodId) return;
    const current = fiscalPeriods.data.items.find(
      (p) => p.postingState === 'open' && p.startDate <= businessDate && p.endDate >= businessDate,
    );
    if (current) {
      setFiscalPeriodId(current.fiscalPeriodId);
    } else {
      const anyOpen = fiscalPeriods.data.items.find((p) => p.postingState === 'open');
      if (anyOpen) setFiscalPeriodId(anyOpen.fiscalPeriodId);
    }
  }, [fiscalPeriods, businessDate, fiscalPeriodId]);

  // When selected invoice changes, update reference & control account
  function handleSelectInvoice(openItemId: string) {
    setOriginalOpenItemId(openItemId);
    if (!openItemId) return;
    if (openInvoices.status === 'ready') {
      const inv = openInvoices.data.items.find((i) => i.openItemId === openItemId);
      if (inv) {
        if (inv.sourceReference) setOriginalInvoiceReference(inv.sourceReference);
        if (inv.controlAccountId) setControlAccountId(inv.controlAccountId);
      }
    }
  }

  // Update line item
  function updateLine(index: number, patch: Partial<SalesReturnFormLine>) {
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== index) return line;
        const updated = { ...line, ...patch };
        // If product selected and no description, populate description
        if (patch.productId && products.status === 'ready') {
          const prod = products.data.items.find((p) => String(p.productId) === patch.productId);
          if (prod && !updated.description) {
            updated.description = String(prod.name ?? '');
          }
        }
        // Auto compute VAT (10% standard rate) if enabled
        if (autoComputeTax && (patch.quantity !== undefined || patch.unitPriceRial !== undefined)) {
          const qty = Number(toEnglishDigits(updated.quantity)) || 0;
          const price = BigInt(parseRialInput(updated.unitPriceRial) || '0');
          if (qty > 0 && price > 0n) {
            const subtotal = (BigInt(Math.round(qty * 100)) * price) / 100n;
            const vat = (subtotal * 10n) / 100n;
            updated.vatAmountRial = vat.toString();
          }
        }
        return updated;
      }),
    );
  }

  function addLine() {
    setLines((prev) => [...prev, { ...EMPTY_RETURN_LINE }]);
  }

  function removeLine(index: number) {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  }

  // Totals calculations
  const totals = useMemo(() => {
    let gross = 0n;
    let vat = 0n;
    for (const line of lines) {
      const qty = Number(toEnglishDigits(line.quantity)) || 0;
      const price = BigInt(parseRialInput(line.unitPriceRial) || '0');
      const lineGross = qty > 0 && price > 0n ? (BigInt(Math.round(qty * 100)) * price) / 100n : 0n;
      const lineVat = BigInt(parseRialInput(line.vatAmountRial) || '0');
      gross += lineGross;
      vat += lineVat;
    }
    const totalCredit = gross + vat;
    return { gross, vat, totalCredit };
  }, [lines]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrorMsg(null);

    if (!partyId) {
      setErrorMsg('لطفاً مشتری (طرف‌حساب) را انتخاب کنید.');
      return;
    }
    if (!businessDate) {
      setErrorMsg('تاریخ اعلامیه برگشت از فروش الزامی است.');
      return;
    }
    if (!fiscalPeriodId) {
      setErrorMsg('دوره مالی مربوطه مشخص نشده است.');
      return;
    }
    if (!controlAccountId) {
      setErrorMsg('حساب معین دریافتنی مشتری الزامی است.');
      return;
    }
    if (!salesReturnAccountId) {
      setErrorMsg('حساب معین برگشت از فروش و تخفیفات الزامی است.');
      return;
    }
    if (!returnReason.trim()) {
      setErrorMsg('علت برگشت از فروش را مشخص کنید.');
      return;
    }
    if (!originalOpenItemId && !originalInvoiceReference.trim()) {
      setErrorMsg('انتخاب یا درج شناسه فاکتور مبدأ الزامی است.');
      return;
    }

    const filledLines = lines.filter((l) => l.description.trim() || l.unitPriceRial);
    if (filledLines.length === 0) {
      setErrorMsg('حداقل یک ردیف کالا یا خدمت مرجوعی الزامی است.');
      return;
    }

    for (let i = 0; i < filledLines.length; i++) {
      const l = filledLines[i]!;
      if (!l.description.trim()) {
        setErrorMsg(`شرح ردیف ${i + 1} الزامی است.`);
        return;
      }
      const qty = Number(toEnglishDigits(l.quantity));
      if (!qty || qty <= 0) {
        setErrorMsg(`تعداد ردیف ${i + 1} باید بزرگتر از صفر باشد.`);
        return;
      }
      const price = BigInt(parseRialInput(l.unitPriceRial) || '0');
      if (price <= 0n) {
        setErrorMsg(`فی ردیف ${i + 1} باید بزرگتر از صفر باشد.`);
        return;
      }
    }

    const selectedPeriod =
      fiscalPeriods.status === 'ready'
        ? fiscalPeriods.data.items.find((p) => p.fiscalPeriodId === fiscalPeriodId)
        : undefined;

    if (!selectedPeriod || selectedPeriod.postingState !== 'open') {
      setErrorMsg('دوره مالی معتبر نیست یا بسته شده است.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        partyId,
        originalOpenItemId: originalOpenItemId || null,
        originalInvoiceReference: originalInvoiceReference.trim() || null,
        businessDate,
        fiscalYearId: selectedPeriod.fiscalYearId,
        fiscalPeriodId: selectedPeriod.fiscalPeriodId,
        returnReason: returnReason.trim(),
        controlAccountId,
        salesReturnAccountId,
        vatAccountId: totals.vat > 0n ? vatAccountId || null : null,
        restock,
        notes: notes.trim() || null,
        lines: filledLines.map((l, idx) => ({
          rowNumber: idx + 1,
          productId: l.productId || null,
          description: l.description.trim(),
          quantity: Number(toEnglishDigits(l.quantity)),
          unitPriceRial: parseRialInput(l.unitPriceRial).toString(),
          vatAmountRial: parseRialInput(l.vatAmountRial).toString(),
        })),
      };

      await apiPost(`${base}/api/v1/finance/ar/sales-returns`, payload);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'خطا در ثبت برگشت از فروش';
      setErrorMsg(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
        overflowY: 'auto',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: 'var(--sk-bg)',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 900,
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          border: '1px solid var(--sk-border)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}>
          <V2CloseButton onClick={onClose} />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--sk-divider)',
            paddingBottom: 12,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
              ثبت برگشت از فروش و اعلامیه بستانکار (Credit Memo)
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              صدور سند حسابداری خودکار (بدهکار: برگشت از فروش و مالیات / بستانکار: حساب دریافتنی
              مشتری) و کاهش مانده بدهی فاکتور.
            </p>
          </div>
          <V2Button variant="ghost" size="sm" onClick={onClose} icon="x">
            بستن
          </V2Button>
        </div>

        {errorMsg && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 'var(--sk-r-md)',
              background: 'var(--sk-color-danger-subtle, rgba(239, 68, 68, 0.1))',
              color: 'var(--sk-color-danger, #ef4444)',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <V2FormGrid>
            {/* 1. Party */}
            <V2FormField label="مشتری (طرف‌حساب)" required width="medium">
              <select
                className="sk-select"
                value={partyId}
                onChange={(e) => {
                  setPartyId(e.target.value);
                  setOriginalOpenItemId('');
                }}
              >
                <option value="">انتخاب مشتری...</option>
                {parties.status === 'ready' &&
                  parties.data.items.map((p) => {
                    const pId = String(p.partyId ?? '');
                    const pName = String(p.name ?? '');
                    const pCode = p.code ? ` (${String(p.code)})` : '';
                    return (
                      <option key={pId} value={pId}>
                        {pName}
                        {pCode}
                      </option>
                    );
                  })}
              </select>
            </V2FormField>

            {/* 2. Original Invoice */}
            <V2FormField label="فاکتور مبدأ مرتبط" width="medium">
              <select
                className="sk-select"
                value={originalOpenItemId}
                onChange={(e) => handleSelectInvoice(e.target.value)}
                disabled={!partyId}
              >
                <option value="">
                  {partyId
                    ? 'انتخاب از فاکتورهای باز مشتری (اختیاری)...'
                    : 'ابتدا مشتری را انتخاب کنید'}
                </option>
                {openInvoices.status === 'ready' &&
                  openInvoices.data.items.map((inv) => (
                    <option key={inv.openItemId} value={inv.openItemId}>
                      {inv.sourceReference || 'بدون شماره'} · مانده:{' '}
                      {formatRialFa(inv.openBalanceRial, unit)} {unitLabel}
                    </option>
                  ))}
              </select>
            </V2FormField>

            {/* 3. Invoice Reference override */}
            <V2FormField label="شماره فاکتور / مرجع" width="short">
              <input
                type="text"
                className="sk-input ltr"
                placeholder="مثلاً INV-1403-010"
                value={originalInvoiceReference}
                onChange={(e) => setOriginalInvoiceReference(e.target.value)}
              />
            </V2FormField>

            {/* 4. Date */}
            <V2FormField label="تاریخ برگشت" required width="short">
              <JalaliDateInput value={businessDate} onChange={setBusinessDate} />
            </V2FormField>

            {/* 5. Fiscal Period */}
            <V2FormField label="دوره مالی" required width="medium">
              <select
                className="sk-select"
                value={fiscalPeriodId}
                onChange={(e) => setFiscalPeriodId(e.target.value)}
              >
                <option value="">انتخاب دوره مالی...</option>
                {fiscalPeriods.status === 'ready' &&
                  fiscalPeriods.data.items.map((p) => (
                    <option
                      key={p.fiscalPeriodId}
                      value={p.fiscalPeriodId}
                      disabled={p.postingState !== 'open'}
                    >
                      {p.name} ({p.postingState === 'open' ? 'باز' : 'بسته'})
                    </option>
                  ))}
              </select>
            </V2FormField>

            {/* 6. Reason */}
            <V2FormField label="علت مرجوعی" required width="wide">
              <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
                <input
                  type="text"
                  className="sk-input"
                  placeholder="علت مرجوعی کالا یا صدور اعلامیه بستانکار"
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {COMMON_RETURN_REASONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setReturnReason(r)}
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        background: 'var(--sk-bg-subtle, rgba(0,0,0,0.05))',
                        border: '1px solid var(--sk-border)',
                        borderRadius: 4,
                        cursor: 'pointer',
                        color: 'var(--sk-fg-muted)',
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </V2FormField>
          </V2FormGrid>

          {/* GL Accounts Section */}
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 'var(--sk-r-md)',
              background: 'var(--sk-bg-subtle, rgba(0,0,0,0.02))',
              border: '1px solid var(--sk-border)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg-muted)' }}>
              حساب‌های معین سند حسابداری
            </span>
            <V2FormGrid>
              <V2FormField label="معین حساب دریافتنی (بستانکار)" required width="medium">
                <select
                  className="sk-select"
                  value={controlAccountId}
                  onChange={(e) => setControlAccountId(e.target.value)}
                >
                  <option value="">انتخاب معین دریافتنی...</option>
                  {postableAccounts.map((a) => (
                    <option key={a.accountId} value={a.accountId}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              </V2FormField>

              <V2FormField label="معین برگشت از فروش (بدهکار)" required width="medium">
                <select
                  className="sk-select"
                  value={salesReturnAccountId}
                  onChange={(e) => setSalesReturnAccountId(e.target.value)}
                >
                  <option value="">انتخاب معین برگشت از فروش...</option>
                  {postableAccounts.map((a) => (
                    <option key={a.accountId} value={a.accountId}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              </V2FormField>

              <V2FormField label="معین ارزش افزوده (اختیاری)" width="medium">
                <select
                  className="sk-select"
                  value={vatAccountId}
                  onChange={(e) => setVatAccountId(e.target.value)}
                >
                  <option value="">بدون تعدیل مالیات (یا انتخاب معین ارزش افزوده)</option>
                  {postableAccounts.map((a) => (
                    <option key={a.accountId} value={a.accountId}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              </V2FormField>
            </V2FormGrid>
          </div>

          {/* Line items table */}
          <div
            style={{
              border: '1px solid var(--sk-border)',
              borderRadius: 'var(--sk-r-md)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                background: 'var(--sk-bg-subtle, rgba(0,0,0,0.03))',
                borderBottom: '1px solid var(--sk-divider)',
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600 }}>اقلام مرجوعی کالا / خدمت</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label
                  style={{
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={autoComputeTax}
                    onChange={(e) => setAutoComputeTax(e.target.checked)}
                  />
                  محاسبه خودکار ۱۰٪ ارزش افزوده
                </label>
                <V2Button type="button" size="sm" variant="secondary" icon="plus" onClick={addLine}>
                  افزودن قلم
                </V2Button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="sk-table" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ width: 40, textAlign: 'center' }}>ردیف</th>
                    <th style={{ width: 180 }}>کالا / خدمت</th>
                    <th>شرح قلم</th>
                    <th style={{ width: 90 }}>تعداد</th>
                    <th style={{ width: 140 }}>فی واحد ({unitLabel})</th>
                    <th style={{ width: 130 }}>مالیات ({unitLabel})</th>
                    <th style={{ width: 140 }}>جمع سطر ({unitLabel})</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => {
                    const qty = Number(toEnglishDigits(line.quantity)) || 0;
                    const price = BigInt(parseRialInput(line.unitPriceRial) || '0');
                    const gross =
                      qty > 0 && price > 0n ? (BigInt(Math.round(qty * 100)) * price) / 100n : 0n;
                    const vat = BigInt(parseRialInput(line.vatAmountRial) || '0');
                    const lineTotal = gross + vat;

                    return (
                      <tr key={idx}>
                        <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                        <td>
                          <select
                            className="sk-select"
                            style={{ fontSize: 12 }}
                            value={line.productId}
                            onChange={(e) => updateLine(idx, { productId: e.target.value })}
                          >
                            <option value="">انتخاب کالا...</option>
                            {products.status === 'ready' &&
                              products.data.items.map((prod) => {
                                const prodId = String(prod.productId ?? '');
                                const prodName = String(prod.name ?? '');
                                return (
                                  <option key={prodId} value={prodId}>
                                    {prodName}
                                  </option>
                                );
                              })}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="sk-input"
                            style={{ fontSize: 12 }}
                            placeholder="شرح کالا یا علت بازگشت"
                            value={line.description}
                            onChange={(e) => updateLine(idx, { description: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="sk-input ltr"
                            style={{ fontSize: 12 }}
                            value={line.quantity}
                            onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="sk-input ltr"
                            style={{ fontSize: 12 }}
                            placeholder="0"
                            value={line.unitPriceRial}
                            onChange={(e) => updateLine(idx, { unitPriceRial: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="sk-input ltr"
                            style={{ fontSize: 12 }}
                            placeholder="0"
                            value={line.vatAmountRial}
                            onChange={(e) => updateLine(idx, { vatAmountRial: e.target.value })}
                          />
                        </td>
                        <td className="ltr sk-num" style={{ fontWeight: 600 }}>
                          {formatRialFa(lineTotal.toString(), unit)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {lines.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLine(idx)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--sk-color-danger, #ef4444)',
                                cursor: 'pointer',
                                padding: 4,
                              }}
                              title="حذف سطر"
                            >
                              <Icon name="x" size="sm" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals Summary */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 24,
                padding: '12px 16px',
                background: 'var(--sk-bg-subtle, rgba(0,0,0,0.02))',
                borderTop: '1px solid var(--sk-divider)',
                fontSize: 13,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>جمع ناخالص: </span>
                <span className="sk-num font-mono" style={{ fontWeight: 600 }}>
                  {formatRialFa(totals.gross.toString(), unit)} {unitLabel}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>مالیات بر ارزش افزوده: </span>
                <span className="sk-num font-mono" style={{ fontWeight: 600 }}>
                  {formatRialFa(totals.vat.toString(), unit)} {unitLabel}
                </span>
              </div>
              <div
                style={{
                  padding: '4px 12px',
                  borderRadius: 'var(--sk-r-md)',
                  background: 'var(--sk-color-success-subtle, rgba(34, 197, 94, 0.1))',
                  color: 'var(--sk-color-success, #16a34a)',
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                <span>مبلغ کل اعلامیه بستانکار: </span>
                <span className="sk-num font-mono">
                  {formatRialFa(totals.totalCredit.toString(), unit)} {unitLabel}
                </span>
              </div>
            </div>
          </div>

          {/* Options: restock & notes */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <label
              style={{
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={restock}
                onChange={(e) => setRestock(e.target.checked)}
              />
              بازگشت کالا به موجودی انبار (Restock)
            </label>
          </div>

          <V2FormField label="توضیحات و یادداشت" width="full">
            <textarea
              className="sk-textarea"
              rows={2}
              placeholder="توضیحات تکمیلی پیرامون مرجوعی، شماره بارنامه یا مکاتبات مشتری..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </V2FormField>

          {/* Action buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <V2Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              انصراف
            </V2Button>
            <V2Button type="submit" variant="primary" icon="check" disabled={submitting}>
              {submitting ? 'در حال ثبت...' : 'ثبت قطعی مرجوعی و صدور سند دوبل'}
            </V2Button>
          </div>
        </form>
      </div>
    </div>
  );
}
